import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import SEO from '../components/SEO';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useCart } from '../context/CartContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { getImageUrl, isVideoPath } from '../utils/imageUrl';
import { guestSavedIds, mergeGuestSaves, setGuestSaved } from '../utils/savedProducts';
import { Skeleton } from '../components/primitives';
import './Wishlist.css';

const Wishlist = () => {
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useCustomerAuth();
  const { addToCart } = useCart();
  const { currency: ctxCurrency, currencyLabel } = useSiteSettings();
  const currency = currencyLabel || ctxCurrency || t('common.currency');
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);

  // Signed-in shoppers see their account wishlist (with anything saved in
  // this browser before sign-in moved into it); guests see this browser's.
  const fetchWishlist = useCallback(async () => {
    setLoading(true);
    if (!isAuthenticated) {
      const ids = guestSavedIds();
      const found = await Promise.allSettled(ids.map((id) => api.get(`/api/products/by-id/${id}`)));
      const rows = found.filter((r) => r.status === 'fulfilled' && r.value?.data?.id).map((r) => ({ id: r.value.data.id, productId: r.value.data.id, product: r.value.data }));
      setItems(rows);
      setCount(rows.length);
      setLoading(false);
      return;
    }
    try {
      await mergeGuestSaves();
      const { data } = await api.get('/api/customer/wishlist');
      setItems(data.items || []);
      setCount(typeof data.count === 'number' ? data.count : (data.items || []).length);
    } catch {
      setItems([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) fetchWishlist();
  }, [authLoading, fetchWishlist]);

  const stockLabel = (status) => {
    if (status === 'in_stock') return t('productDetail.inStock');
    if (status === 'low_stock') return t('productDetail.lowStock');
    return t('productDetail.outOfStock');
  };

  const stockClass = (status) => {
    if (status === 'in_stock') return 'wishlist-stock wishlist-stock--in';
    if (status === 'low_stock') return 'wishlist-stock wishlist-stock--low';
    return 'wishlist-stock wishlist-stock--out';
  };

  const handleRemove = async (productId) => {
    setRemovingId(productId);
    try {
      if (isAuthenticated) await api.delete(`/api/customer/wishlist/${productId}`);
      else setGuestSaved(productId, false);
      setItems((prev) => {
        const next = prev.filter((row) => row.productId !== productId);
        setCount(next.length);
        return next;
      });
    } catch {
      // keep list on failure
    } finally {
      setRemovingId(null);
    }
  };

  const fmtPrice = (v) =>
    parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  if (authLoading) {
    return (
      <div className="wishlist-page" aria-busy="true">
        <div className="container">
          <Skeleton.Block height="2rem" width="220px" rounded="md" style={{ marginBottom: 'var(--space-4)' }} />
          <Skeleton.Line width="40%" />
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <SEO
        title={t('wishlist.title')}
        description={t('wishlist.emptyMessage')}
        keywords={['wishlist', 'saved', 'toys']}
      />

      <header className="wishlist-header">
        <h1 className="wishlist-title">{t('wishlist.title')}</h1>
        {count > 0 && (
          <p className="wishlist-subtitle">{t('wishlist.count', { count })}</p>
        )}
        {!isAuthenticated && count > 0 && (
          <p className="wishlist-subtitle">
            <Link to="/login" state={{ from: '/wishlist' }}>{t('wishlist.signInToKeep', 'Sign in to keep them on all your devices.')}</Link>
          </p>
        )}
      </header>

      {loading ? (
        <div className="wishlist-grid" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton.Card key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="wishlist-empty">
          <h2 className="wishlist-empty-title">{t('wishlist.empty')}</h2>
          <p className="wishlist-empty-message">{t('wishlist.emptyMessage')}</p>
          <Link to="/products" className="wishlist-empty-link">
            {t('account.startShopping')}
          </Link>
        </div>
      ) : (
        <div className="wishlist-grid">
          {items.map((row) => {
            const product = row.product;
            if (!product) return null;
            const img = product.images && product.images[0];
            const canAdd =
              product.active !== false && product.stockStatus !== 'out_of_stock';

            return (
              <article key={row.id} className="wishlist-card">
                <div className="wishlist-card-media">
                  <Link to={`/products/${product.slug}`} className="wishlist-card-link">
                    {img && !isVideoPath(img) ? (
                      <img
                        src={getImageUrl(img, { size: 'thumb' })}
                        alt={product.name}
                        loading="lazy"
                        onError={(e) => {
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = '1';
                            e.target.src = getImageUrl(img);
                          }
                        }}
                      />
                    ) : img ? (
                      <img src={getImageUrl(img)} alt={product.name} loading="lazy" />
                    ) : (
                      <div className="wishlist-card-placeholder" />
                    )}
                  </Link>
                  {product.stockStatus && (
                    <span className={stockClass(product.stockStatus)}>
                      {stockLabel(product.stockStatus)}
                    </span>
                  )}
                </div>
                <div className="wishlist-card-body">
                  <Link to={`/products/${product.slug}`} className="wishlist-card-name">
                    {product.name}
                  </Link>
                  <div className="wishlist-card-price">
                    {fmtPrice(product.price)} {currency}
                  </div>
                  <div className="wishlist-actions">
                    <button
                      type="button"
                      className="wishlist-btn wishlist-btn--primary"
                      disabled={!canAdd}
                      onClick={() => addToCart(product)}
                    >
                      {t('wishlist.addToCart')}
                    </button>
                    <button
                      type="button"
                      className="wishlist-btn wishlist-btn--ghost"
                      disabled={removingId === product.id}
                      onClick={() => handleRemove(product.id)}
                    >
                      {t('wishlist.remove')}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Wishlist;
